import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { toast } from "sonner";
import type { CircleApplication } from "@/types/circle";

export function useAdminCircleApplications() {
  const { userId } = useAuth();
  const queryClient = useQueryClient();

  const { data: applications, isLoading, error } = useQuery({
    queryKey: ["admin-circle-applications"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("circle_applications")
        .select(`
          *,
          user:profiles!circle_applications_user_id_fkey(
            id,
            first_name,
            last_name,
            email,
            avatar_url
          )
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as CircleApplication[];
    },
  });

  const approveApplication = useMutation({
    mutationFn: async (applicationId: string) => {
      if (!userId) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("circle_applications")
        .update({
          status: "approved",
          reviewed_by: userId,
          reviewed_at: new Date().toISOString(),
        })
        .eq("id", applicationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-circle-applications"] });
      queryClient.invalidateQueries({ queryKey: ["circle-stats"] });
      toast.success("Application approved!");
    },
    onError: (error) => {
      console.error("Error approving application:", error);
      toast.error("Failed to approve application");
    },
  });

  const rejectApplication = useMutation({
    mutationFn: async ({ applicationId, reason }: { applicationId: string; reason?: string }) => {
      if (!userId) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("circle_applications")
        .update({
          status: "rejected",
          reviewed_by: userId,
          reviewed_at: new Date().toISOString(),
          rejection_reason: reason || null,
        })
        .eq("id", applicationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-circle-applications"] });
      queryClient.invalidateQueries({ queryKey: ["circle-stats"] });
      toast.success("Application rejected");
    },
    onError: (error) => {
      console.error("Error rejecting application:", error);
      toast.error("Failed to reject application");
    },
  });

  const deleteApplication = useMutation({
    mutationFn: async (applicationId: string) => {
      const { error } = await supabase
        .from("circle_applications")
        .delete()
        .eq("id", applicationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-circle-applications"] });
      queryClient.invalidateQueries({ queryKey: ["circle-stats"] });
      toast.success("Application deleted");
    },
    onError: (error) => {
      console.error("Error deleting application:", error);
      toast.error("Failed to delete application");
    },
  });

  return {
    applications: applications || [],
    isLoading,
    error,
    approveApplication,
    rejectApplication,
    deleteApplication,
  };
}
